"use client";
import { Room, RemoteParticipant } from "livekit-client";
import { Reaction, ParticipantReaction } from "@client/types/meet.types";
import { useState, useCallback, useRef, useMemo, useEffect } from "react";

interface UseReactionsOptions {
  room: Room;
}

interface UseReactionsReturn {
  isHandRaised: boolean;
  reactions: Reaction[];
  participantReactions: Map<string, ParticipantReaction>;
  toggleHandRaise: () => void;
  sendReaction: (emoji: string) => void;
  handleReactionDataReceived: (message: Record<string, unknown>, participant?: RemoteParticipant) => void;
}

const REACTION_DURATION_MS = 3000;
const PUBLISH_OPTIONS = { reliable: true } as const;

const textEncoder = new TextEncoder();

const publishData = (room: Room, data: object) => {
  room.localParticipant.publishData(textEncoder.encode(JSON.stringify(data)), PUBLISH_OPTIONS);
};

/**
 * Manages participant reactions (emoji) and hand raising.
 */
export const useReactions = ({
  room
}: UseReactionsOptions): UseReactionsReturn & {
  lowerHand: () => void;
  requestLowerHand: (participantId: string) => void;
} => {
  const [isHandRaised, setIsHandRaised] = useState(false);
  const [reactions, setReactions] = useState<Reaction[]>([]);
  const [participantReactions, setParticipantReactions] = useState<Map<string, ParticipantReaction>>(() => new Map());

  // Stable room reference
  const roomRef = useRef(room);
  useEffect(() => {
    roomRef.current = room;
  }, [room]);

  const lowerHand = useCallback(() => {
    const currentRoom = roomRef.current;
    const localParticipant = currentRoom.localParticipant;

    setIsHandRaised(false);

    // Update local participant reaction state
    setParticipantReactions((prevReactions) => {
      const newMap = new Map(prevReactions);
      newMap.set(localParticipant.sid, {
        participantId: localParticipant.sid,
        isHandRaised: false,
        handRaisedAt: undefined
      });
      return newMap;
    });

    // Broadcast hand lower
    publishData(currentRoom, {
      type: "hand-raise",
      raised: false,
      participantId: localParticipant.sid,
      participantName: localParticipant.name || "Unknown",
      handRaisedAt: null
    });
  }, []);

  const toggleHandRaise = useCallback(() => {
    const currentRoom = roomRef.current;
    const localParticipant = currentRoom.localParticipant;

    setIsHandRaised((prev) => {
      const newState = !prev;
      const handRaisedAt = newState ? Date.now() : undefined;

      // Update local participant reaction state
      setParticipantReactions((prevReactions) => {
        const newMap = new Map(prevReactions);
        newMap.set(localParticipant.sid, {
          participantId: localParticipant.sid,
          isHandRaised: newState,
          handRaisedAt
        });
        return newMap;
      });

      // Broadcast hand raise/lower
      publishData(currentRoom, {
        type: "hand-raise",
        raised: newState,
        participantId: localParticipant.sid,
        participantName: localParticipant.name || "Unknown",
        handRaisedAt: handRaisedAt ?? null
      });

      return newState;
    });
  }, []);

  const requestLowerHand = useCallback((participantId: string) => {
    const currentRoom = roomRef.current;
    // content, options
    const encoder = new TextEncoder();
    const data = encoder.encode(JSON.stringify({ type: "lower-hand-request", targetSid: participantId }));

    // Send to specific participant
    currentRoom.localParticipant.publishData(data, {
      reliable: true,
      destinationSids: [participantId]
    } as any);
  }, []);

  const sendReaction = useCallback((emoji: string) => {
    const currentRoom = roomRef.current;
    const localParticipant = currentRoom.localParticipant;
    const timestamp = Date.now();

    const newReaction: Reaction = {
      participantId: localParticipant.sid,
      participantName: localParticipant.name || "You",
      emoji,
      timestamp
    };

    setReactions((prev) => [...prev, newReaction]);

    // Auto-remove reaction after duration
    setTimeout(() => {
      setReactions((prev) => prev.filter((r) => r.timestamp !== timestamp));
    }, REACTION_DURATION_MS);

    publishData(currentRoom, {
      type: "reaction",
      emoji,
      participantName: localParticipant.name || "Unknown"
    });
  }, []);

  const handleReactionDataReceived = useCallback(
    (message: Record<string, unknown>, participant?: RemoteParticipant) => {
      const { type, emoji, raised, participantName, targetSid, handRaisedAt } = message as {
        type: string;
        emoji?: string;
        raised?: boolean;
        participantName?: string;
        targetSid?: string;
        handRaisedAt?: number | null;
      };

      if (type === "reaction" && emoji && participant) {
        const timestamp = Date.now();

        setReactions((prev) => [
          ...prev,
          {
            participantId: participant.sid,
            participantName: participantName || participant.name || "Unknown",
            emoji,
            timestamp
          }
        ]);

        setTimeout(() => {
          setReactions((prev) => prev.filter((r) => r.timestamp !== timestamp));
        }, REACTION_DURATION_MS);
      } else if (type === "hand-raise" && participant) {
        setParticipantReactions((prev) => {
          const newMap = new Map(prev);
          const isRaised = raised ?? false;
          newMap.set(participant.sid, {
            participantId: participant.sid,
            isHandRaised: isRaised,
            handRaisedAt: isRaised ? (typeof handRaisedAt === "number" ? handRaisedAt : Date.now()) : undefined
          });
          return newMap;
        });
      } else if (type === "lower-hand-request") {
        // If we receive this request, we are being asked to lower our hand
        // Check if it is meant for us (robustness against broadcast)
        const currentRoom = roomRef.current;
        if (targetSid === currentRoom.localParticipant.sid) {
          lowerHand();
        }
      }
    },
    [lowerHand]
  );

  return useMemo(
    () => ({
      isHandRaised,
      reactions,
      participantReactions,
      toggleHandRaise,
      lowerHand,
      requestLowerHand,
      sendReaction,
      handleReactionDataReceived
    }),
    [
      isHandRaised,
      reactions,
      participantReactions,
      toggleHandRaise,
      lowerHand,
      requestLowerHand,
      sendReaction,
      handleReactionDataReceived
    ]
  );
};
